/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

// Every Thai province, district and subdistrict with its postcode, as the
// jobs service hands it out (Forms/address/thai-address.json there, from
// kongvut/thai-province-data, MIT). The address field picks from it; the
// service checks what was picked against the same file.

import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { AppConfigService } from '@alfresco/adf-core';
import { Observable } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';

/** The file as sent: [province, [district, [subdistrict, postcode][]][]][] */
export interface ThaiAddressFile {
  source: string;
  provinces: [string, [string, [string, number][]][]][];
}

/** One subdistrict, with where it is. */
export interface ThaiPlace {
  subdistrict: string;
  district: string;
  province: string;
  postcode: string;
}

const thai = (a: string, b: string): number => a.localeCompare(b, 'th');

/** Words people put before a name ("ต.บางพลีใหญ่", "อำเภอบางพลี") that are not part of it. */
const PREFIXES = /^(ตำบล|ต\.|แขวง|อำเภอ|อ\.|เขต|จังหวัด|จ\.)/;

/** The list, sorted the Thai way, ready to pick from and to search. */
export class ThaiAddressBook {
  private readonly tree = new Map<string, Map<string, ThaiPlace[]>>();
  private readonly places: (ThaiPlace & { haystack: string })[] = [];
  readonly provinces: string[];

  constructor(file: ThaiAddressFile) {
    for (const [province, districts] of file.provinces) {
      const byDistrict = new Map<string, ThaiPlace[]>();
      for (const [district, subdistricts] of [...districts].sort((a, b) => thai(a[0], b[0]))) {
        const places = subdistricts
          .map(([subdistrict, postcode]) => ({ subdistrict, district, province, postcode: String(postcode) }))
          .sort((a, b) => thai(a.subdistrict, b.subdistrict));
        byDistrict.set(district, places);
        this.places.push(...places.map((p) => ({ ...p, haystack: `${p.subdistrict} ${p.district} ${p.province} ${p.postcode}` })));
      }
      this.tree.set(province, byDistrict);
    }
    // Bangkok first, as every Thai list puts it; then the rest in Thai order
    this.provinces = [...this.tree.keys()].sort((a, b) => (a === 'กรุงเทพมหานคร' ? -1 : b === 'กรุงเทพมหานคร' ? 1 : thai(a, b)));
  }

  districts(province: string): string[] {
    return [...(this.tree.get(province)?.keys() ?? [])];
  }

  subdistricts(province: string, district: string): ThaiPlace[] {
    return this.tree.get(province)?.get(district) ?? [];
  }

  find(province: string, district: string, subdistrict: string): ThaiPlace | undefined {
    return this.subdistricts(province, district).find((p) => p.subdistrict === subdistrict);
  }

  /**
   * Subdistricts matching what was typed: each word somewhere in the
   * subdistrict, district, province or postcode ("บางพลี 10540", "ต.ศรีภูมิ
   * เชียงใหม่"). Those whose subdistrict starts with the first word come first.
   */
  search(text: string, limit = 10): ThaiPlace[] {
    const words = text
      .trim()
      .split(/\s+/)
      .map((w) => w.replace(PREFIXES, ''))
      .filter((w) => w.length > 0);
    if (!words.length || (words.length === 1 && words[0].length < 2)) {
      return [];
    }
    const found = this.places.filter((p) => words.every((w) => p.haystack.includes(w)));
    const first = words[0];
    const rank = (p: ThaiPlace) => (p.subdistrict.startsWith(first) ? 0 : p.postcode.startsWith(first) ? 1 : p.district.startsWith(first) ? 2 : 3);
    return found
      .sort((a, b) => rank(a) - rank(b))
      .slice(0, limit)
      .map(({ subdistrict, district, province, postcode }) => ({ subdistrict, district, province, postcode }));
  }
}

/** Loads the list once, the first time an address field needs it. */
@Injectable({ providedIn: 'root' })
export class ThaiAddressService {
  private readonly http = inject(HttpClient);
  private readonly appConfig = inject(AppConfigService);
  private book$?: Observable<ThaiAddressBook>;

  book(): Observable<ThaiAddressBook> {
    if (!this.book$) {
      const url = `${this.appConfig.get<string>('jobs.baseUrl', '/jobs-api')}/api/forms/thai-address`;
      this.book$ = this.http.get<ThaiAddressFile>(url).pipe(
        map((file) => new ThaiAddressBook(file)),
        shareReplay({ bufferSize: 1, refCount: false })
      );
    }
    return this.book$;
  }

  /** After a failure, so the next field to ask tries again. */
  forget(): void {
    this.book$ = undefined;
  }
}
